// src/settingComponent/apiSetting/apiPages/ApiBusinessTerms.js
import React from 'react';
import { Helmet } from 'react-helmet';
import '../../../unAuthPages/TermsOfService.css';
import Footer from '../../../unAuthPages/Footer';

const ApiBusinessTerms = () => {
  return (
    <div className="terms-container">
      <Helmet>
        <title>Bumpups: API Business Terms</title>
        <meta
          name="description"
          content="Review the Bumpups API Business Terms for developer use of our video analysis services, APIs, and tools."
        />
      </Helmet>

      <h1 className="terms-title">Bumpups API Terms of Service</h1>
      <div className="terms-text-container">
        <p><strong>Last Updated:</strong> June 8, 2025</p>

        <h3>Introduction</h3>
        <p>
          These Bumpups API Business Terms (“Business Terms”) govern your (“Customer”) use of Bumpups Inc.’s (“Bumpups”) APIs, video analysis services, developer tools, and related offerings (collectively, the “Services”). By accessing or using our Services, including by integrating our APIs into your applications (“Customer Applications”), you agree to be bound by these Business Terms as well as any additional policies or guidelines provided by Bumpups (collectively, the “Agreement”). If you are entering into these terms on behalf of an organization, you represent that you have the legal authority to bind that organization.
        </p>

        <h3>1. Services</h3>
        <h4>1.1 Use of Services</h4>
        <p>
          Bumpups grants you a non-exclusive, non-transferable right to access and use the Services during the term of this Agreement. This includes using our APIs to integrate our AI-powered video analysis and insights into your Customer Applications and making such applications available to your end users (“End Users”).
        </p>
        <h4>1.2 Third-Party Offerings</h4>
        <p>
          Third parties may offer products, services, or content (“Third-Party Offerings”) via the Services. Your use of any Third-Party Offering is subject both to these Business Terms and any additional terms provided by the third party.
        </p>
        <h4>1.3 Account Responsibilities</h4>
        <p>
          You must provide accurate and up-to-date account information and are responsible for all activity under your account—including that of any End Users who access the Services through your Customer Applications. You agree not to share, resell, or lease your account credentials.
        </p>

        <h3>2. Restrictions</h3>
        <p>
          Bumpups retains all right, title, and interest in and to the Services. You only receive the rights expressly granted herein. You agree not to:
        </p>
        <ul>
          <li>Use the Services in any way that violates applicable laws or Bumpups’ policies.</li>
          <li>Infringe on any third party’s intellectual property or proprietary rights.</li>
          <li>Provide personal data of minors or allow unauthorized minors to use the Services.</li>
          <li>Reverse engineer, decompile, or otherwise attempt to discover the underlying source code or structure of the Services.</li>
          <li>Use any data output by the Services to build competitive AI models, except as expressly permitted.</li>
          <li>Extract data from the Services by any method other than the provided APIs.</li>
          <li>Buy, sell, or otherwise transfer your API keys to third parties.</li>
          <li>Use the Services to process, distribute, or promote any video content that is illegal, explicitly pornographic, or otherwise violates public policy or applicable law. Bumpups reserves the right to suspend or terminate access to the Services if such use is detected.</li>
        </ul>

        <h3>3. Customer Content</h3>
        <h4>3.1 Ownership</h4>
        <p>
          You retain all rights in any input you provide (“Input”), and you own all output (“Output”) generated by the Services based on that Input. Bumpups hereby assigns to you any rights it may have in the Output.
        </p>
        <h4>3.2 Bumpups’ Use of Customer Content</h4>
        <p>
          Bumpups will process and store Customer Content in accordance with our privacy commitments and will only use it as necessary to provide and improve the Services, comply with applicable law, and enforce these Business Terms.
        </p>
        <h4>3.3 Customer Obligations</h4>
        <p>
          You are solely responsible for obtaining all necessary rights and permissions for the Input you submit and for evaluating the accuracy and appropriateness of the Output generated by the Services.
        </p>
        <h4>3.4 Uniqueness</h4>
        <p>
          Due to the nature of AI-generated Output, similar content may be produced for different users. The Output provided specifically for you is considered your property, but it does not preclude similar outputs for other users.
        </p>

        <h3>4. Confidentiality</h3>
        <h4>4.1 Definition</h4>
        <p>
          “Confidential Information” includes any business, technical, or financial information disclosed by either party that is marked or reasonably understood to be confidential. This includes Customer Content.
        </p>
        <h4>4.2 Obligations</h4>
        <p>
          Each party agrees to use the other’s Confidential Information solely to perform its obligations under this Agreement, to take reasonable measures to protect it, and not to disclose it to third parties except as permitted herein.
        </p>
        <h4>4.3 Exceptions</h4>
        <p>
          The confidentiality obligations do not extend to information that (a) is publicly known, (b) was known prior to disclosure, (c) is received from a third party without breach of any obligation, or (d) is independently developed.
        </p>

        <h3>5. Security</h3>
        <h4>5.1 Security Program</h4>
        <p>
          Bumpups will maintain and enforce reasonable information security measures designed to protect the Services and Customer Content against unauthorized access, loss, or disclosure.
        </p>
        <h4>5.2 Best Practices</h4>
        <p>
          As part of its security program, Bumpups implements multi-factor authentication for its personnel, monitors network access, and conducts regular risk assessments to minimize security risks.
        </p>

        <h3>6. Privacy</h3>
        <h4>6.1 Personal Data</h4>
        <p>
          If you use the Services to process personal data, you must comply with all applicable privacy laws and provide appropriate notices and consents. If processing personal data, you may be required to execute a Data Processing Addendum with Bumpups.
        </p>
        <h4>6.2 Health Information</h4>
        <p>
          You agree not to use the Services to process any Protected Health Information as defined under applicable health privacy laws unless you have entered into an appropriate Healthcare Addendum with Bumpups.
        </p>

        <h3>7. Payment; Taxes</h3>
        <h4>7.1 Fees and Billing</h4>
        <p>
          You agree to pay all fees associated with your account as set forth on Bumpups’ Pricing Page or any applicable Order Form. Fees are billed in U.S. dollars and are due upon invoice issuance unless otherwise agreed.
        </p>
        <h4>7.2 Service Credits</h4>
        <p>
          If applicable, you may prepay for the Services via Service Credits or receive promotional credits, which are subject to separate terms.
        </p>
        <h4>7.3 Taxes</h4>
        <p>
          All fees are exclusive of taxes, which you are responsible for unless Bumpups is required to collect them.
        </p>
        <h4>7.4 Invoice Disputes</h4>
        <p>
          To dispute any invoice, contact us at{' '}
          <a href="mailto:contact@bumpups.com" className="policyLinks">
            contact@bumpups.com
          </a>{' '}
          within thirty (30) days of issuance. Overdue amounts may incur a finance charge, and services may be suspended if payment is not received.
        </p>
        <h4>7.5 Refund Policy</h4>
        <p>
          All funds deposited into your credit balance are strictly non-refundable. Once money is loaded into your account, it cannot be withdrawn, exchanged, transferred, or refunded under any circumstances. By adding funds to your account, you acknowledge and accept that these funds may only be utilized for the purchase or usage of services offered by our platform. In exceptional circumstances where there is a technical error or billing discrepancy, please contact our support team promptly. Decisions regarding refunds in these special circumstances will be made solely at our discretion and evaluated on a case-by-case basis.
        </p>

        <h3>8. Term and Termination</h3>
        <h4>8.1 Term</h4>
        <p>
          This Agreement commences upon your acceptance and continues until terminated by either party. For recurring subscriptions, your subscription will automatically renew unless either party gives written notice at least thirty (30) days before the renewal date.
        </p>
        <h4>8.2 Termination</h4>
        <p>
          Either party may terminate this Agreement upon written notice if the other party materially breaches any term and fails to cure the breach within thirty (30) days. Bumpups may also suspend or terminate access to the Services to prevent security risks or repeated violations.
        </p>
        <h4>8.3 Effect of Termination</h4>
        <p>
          Termination does not relieve you of any obligations incurred prior to termination. Upon termination, Bumpups will delete your Customer Content within 30 days unless retention is required by law.
        </p>

        <h3>9. Warranties and Disclaimer</h3>
        <h4>9.1 Limited Warranty</h4>
        <p>
          Bumpups warrants that the Services will materially conform to our published documentation when used in accordance with this Agreement during the term.
        </p>
        <h4>9.2 Disclaimer</h4>
        <p>
          Except as expressly provided above, the Services are provided “as is” and without any warranties, whether express or implied. Bumpups disclaims all warranties, including without limitation any implied warranties of merchantability or fitness for a particular purpose.
        </p>

        <h3>10. Indemnification</h3>
        <h4>10.1 Your Indemnity</h4>
        <p>
          You agree to indemnify, defend, and hold harmless Bumpups, its affiliates, and its licensors from any claims, damages, or costs (including reasonable attorneys’ fees) arising out of your use of the Services or any violation of these Business Terms.
        </p>
        <h4>10.2 Bumpups’ Indemnity</h4>
        <p>
          Bumpups shall have no obligation to defend, indemnify, or hold you harmless from any third-party claims arising out of or related to your use of the Services.
        </p>

        <h3>11. Limitation of Liability</h3>
        <h4>11.1 Indirect Damages</h4>
        <p>
          Neither party shall be liable for any indirect, consequential, or punitive damages arising out of this Agreement, except in cases of gross negligence or willful misconduct.
        </p>
        <h4>11.2 Liability Cap</h4>
        <p>
          Except as otherwise noted, each party’s total liability under this Agreement shall not exceed the total amount paid by you in the twelve (12) months preceding the claim.
        </p>

        <h3>12. Trade Controls</h3>
        <p>
          You agree to comply with all applicable trade and export control laws. The Services may not be used in any country or by any entity that is subject to U.S. embargo or similar restrictions.
        </p>

        <h3>13. Dispute Resolution</h3>
        <h4>13.1 Mandatory Arbitration</h4>
        <p>
          Any disputes arising out of or relating to this Agreement shall be resolved exclusively through final and binding arbitration in accordance with the rules of an agreed-upon arbitration provider. The arbitration will be conducted in Dallas, Texas, unless otherwise mutually agreed.
        </p>
        <h4>13.2 Informal Resolution</h4>
        <p>
          Before initiating arbitration, both parties agree to attempt to resolve disputes informally by contacting one another via{' '}
          <a href="mailto:contact@bumpups.com" className="policyLinks">
            contact@bumpups.com
          </a>
          . If unresolved within 60 days, arbitration may commence.
        </p>
        <h4>13.3 Class Action Waiver</h4>
        <p>
          Any claims brought pursuant to this Agreement must be asserted on an individual basis and not as part of a class, consolidated, or representative proceeding.
        </p>

        <h3>14. Modifications</h3>
        <p>
          Bumpups reserves the right to update or modify these Business Terms upon reasonable notice, including by posting updates on our website. Continued use of the Services after an update constitutes your acceptance of the revised terms. Updates will not retroactively apply to disputes arising prior to the change.
        </p>

        <h3>15. Miscellaneous</h3>
        <p>
          15.1 Headings: Headings are for convenience only and do not affect the interpretation of these Business Terms.<br/>
          15.2 Feedback: Any feedback provided by you regarding the Services is non-confidential and may be used by Bumpups without any obligation of compensation or attribution.<br/>
          15.3 Publicity: You may reference your use of the Services in accordance with Bumpups’ Brand Guidelines. Bumpups will not use your name or logo without your prior written consent.<br/>
          15.4 Entire Agreement: This Agreement constitutes the entire understanding between you and Bumpups regarding the Services and supersedes any prior agreements or communications.<br/>
          15.5 Relationship: The parties are independent contractors. Nothing in this Agreement creates a partnership, joint venture, or agency relationship.<br/>
          15.6 Assignment: Neither party may assign this Agreement without the other party’s written consent, except to a successor in connection with a merger or acquisition, provided that such successor assumes all obligations hereunder.<br/>
          15.7 Notices: All notices under this Agreement shall be in writing and sent to the following addresses (or such other address as may be provided in writing):<br/>
          &nbsp;&nbsp;For Bumpups:<br/>
          &nbsp;&nbsp;Bumpups Inc.<br/>
          &nbsp;&nbsp;5473 Blair Rd Ste 100 #112725<br/>
          &nbsp;&nbsp;Dallas, TX 75231-4227<br/>
          &nbsp;&nbsp;Email: <a href="mailto:contact@bumpups.com" className="policyLinks">contact@bumpups.com</a><br/>
        </p>

        <h3>16. Force Majeure</h3>
        <p>
          Neither party shall be liable for any failure or delay in performing its obligations under this Agreement if such failure or delay is caused by events beyond its reasonable control, including but not limited to natural disasters, acts of war, pandemics, or significant outages of third-party services (such as Google Cloud Platform) or other events outside the affected party’s control. The affected party shall promptly notify the other of any such event and use reasonable efforts to resume performance as soon as possible.
        </p>

        <p>
          By using the Services, you acknowledge that you have read, understood, and agree to be bound by these Bumpups API Business Terms.
        </p>
      </div>

      <Footer />
    </div>
  );
};

export default ApiBusinessTerms;
